import { usePost } from "@albertine/api-client";
import { Lib } from "albertine-shared-web";
import { Button } from "@/components/ui/button";
import Icon from "../../Common/Icon";
import "./PaymentItem.css";
import type { Payment, PaymentStatus } from "@albertine/types/openapi";
import classNames from "classnames";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { convertPriceToString } from "../../../common/currency";
import { EventEmitter } from "../../../EventEmitter";
import { useClaims } from "../../../utils/claims.util";
import { dateWithShortMonthFormat, defaultTimeFormat } from "../../../utils/time.util";
import { openLinkInNewTab, scrollToElement } from "../../../utils/window.util";
import Label from "../../Common/Label";
import { Badge } from "../../ui/badge";
import { PaymentConfirmDialog } from "./PaymentConfirmDialog";

export type PaymentItemProps = {
    payment: Payment;
    inModal?: boolean;
    /** Called after a successful admin charge or mark-as-paid so the parent can refetch payments. */
    onChargeComplete?: () => void;
};

export function PaymentItem({
    payment,
    inModal = false,
    onChargeComplete,
}: PaymentItemProps) {
    const {
        id,
        amount,
        currency,
        otherDetails,
        supplierAndCommission,
        invoiceName,
        createdByName,
        createdAt,
        slackUrl,
        status,
        bookingId,
        paymentMethod,
    } = payment;

    const { isAdmin } = useClaims();
    const [chargeConfirmOpen, setChargeConfirmOpen] = useState(false);
    const [chargeError, setChargeError] = useState<string | null>(null);
    const [markAsPaidConfirmOpen, setMarkAsPaidConfirmOpen] = useState(false);
    const [markAsPaidError, setMarkAsPaidError] = useState<string | null>(null);

    const { mutate: chargeBookingPayment, isPending: isChargePending } = usePost(
        "chargeBookingPayment",
        {
            onSuccess: () => {
                setChargeError(null);
                setChargeConfirmOpen(false);
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: "Payment charged successfully",
                        type: "info",
                    },
                });
                onChargeComplete?.();
            },
            onError: (error) => {
                setChargeError(error.error || "Failed to charge payment");
            },
        },
    );

    const { mutate: markBookingPaymentAsPaid, isPending: isMarkAsPaidPending } =
        usePost("markBookingPaymentAsPaid", {
            onSuccess: () => {
                setMarkAsPaidError(null);
                setMarkAsPaidConfirmOpen(false);
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: "Payment marked as paid",
                        type: "info",
                    },
                });
                onChargeComplete?.();
            },
            onError: (error) => {
                setMarkAsPaidError(error.error || "Failed to mark payment as paid");
            },
        });

    const date = format(new Date(createdAt ?? new Date()), dateWithShortMonthFormat);
    const time = format(new Date(createdAt ?? new Date()), defaultTimeFormat);
    const PriceLabel = inModal ? Label.Medium.Normal : Label.Medium.SemiBold;
    const price = { amount, currency };
    const [highlighted, setHighlighted] = useState(false);

    useEffect(() => {
        if (!inModal) {
            const hash = window.location.hash;

            if (hash === `#${id}`) {
                scrollToElement(`payment-request-${id}`, true, true, () => {
                    setHighlighted(true);
                });
            }
        }
    }, [id, inModal]);

    const showChargeButton =
        isAdmin && status === "DRAFT_INVOICE_CREATED" && Boolean(bookingId);
    const showMarkAsPaidButton =
        isAdmin &&
        paymentMethod === "other" &&
        status === "PAYMENT_REQUESTED" &&
        Boolean(bookingId);

    function handleCharge() {
        if (!bookingId) {
            return;
        }
        setChargeError(null);
        chargeBookingPayment({
            param: { bookingId, paymentId: id },
        });
    }

    function handleMarkAsPaid() {
        if (!bookingId) {
            return;
        }
        setMarkAsPaidError(null);
        markBookingPaymentAsPaid({
            param: { bookingId, paymentId: id },
        });
    }

    function handleChargeDialogOpenChange(open: boolean) {
        setChargeConfirmOpen(open);
        if (!open) {
            setChargeError(null);
        }
    }

    function handleMarkAsPaidDialogOpenChange(open: boolean) {
        setMarkAsPaidConfirmOpen(open);
        if (!open) {
            setMarkAsPaidError(null);
        }
    }

    return (
        <div
            id={`payment-request-${id}`}
            className={classNames("payment-request-item", {
                "payment-request-item--in-modal": inModal,
                "payment-request-item--highlighted": highlighted,
            })}
        >
            <Lib.Flex.Row gap="value20" alignItems="center">
                {!inModal && (
                    <div className="payment-request-item__icon-container">
                        <Icon.Check.Medium.P50 />
                    </div>
                )}
                <Lib.Flex.Column gap="value4">
                    <Lib.Flex.Row justifyContent="space-between" alignItems="start">
                        <Lib.Flex.Row gap="value8" alignItems="center">
                            <Label.Medium.SemiBold>
                                {`Payment request on ${date}`}
                            </Label.Medium.SemiBold>
                            <Badge variant="outline">
                                {formatPaymentStatus(status)}
                            </Badge>
                        </Lib.Flex.Row>
                        <PriceLabel className="payment-request-item__amount">
                            {inModal
                                ? `– ${convertPriceToString(price)}`
                                : convertPriceToString(price)}
                        </PriceLabel>
                    </Lib.Flex.Row>

                    {invoiceName && (
                        <Label.Small className="payment-request-item__details">
                            Bill to: {invoiceName}
                        </Label.Small>
                    )}

                    {supplierAndCommission && !inModal && (
                        <Label.Small className="payment-request-item__details">
                            {supplierAndCommission}
                        </Label.Small>
                    )}

                    {otherDetails && (
                        <Label.Small className="payment-request-item__details">
                            {otherDetails}
                        </Label.Small>
                    )}

                    <Lib.Flex.Row justifyContent="space-between" alignItems="center">
                        {createdByName && date && (
                            <Label.Small className="payment-request-item__footer">
                                {createdByName} · {date}, {time}
                            </Label.Small>
                        )}

                        <div className="flex flex-row items-center gap-3">
                            {showChargeButton && (
                                <Button
                                    variant="ghost"
                                    className="payment-request-item__slack-link"
                                    disabled={isChargePending}
                                    type="button"
                                    onClick={() => {
                                        setChargeError(null);
                                        setChargeConfirmOpen(true);
                                    }}
                                >
                                    <Label.Small.SemiBold className="payment-request-item__slack-link-text">
                                        {isChargePending ? "Charging…" : "Charge"}
                                    </Label.Small.SemiBold>
                                </Button>
                            )}
                            {showMarkAsPaidButton && (
                                <Button
                                    variant="ghost"
                                    className="payment-request-item__slack-link"
                                    disabled={isMarkAsPaidPending}
                                    type="button"
                                    onClick={() => {
                                        setMarkAsPaidError(null);
                                        setMarkAsPaidConfirmOpen(true);
                                    }}
                                >
                                    <Label.Small.SemiBold className="payment-request-item__slack-link-text">
                                        {isMarkAsPaidPending
                                            ? "Marking…"
                                            : "Mark as paid"}
                                    </Label.Small.SemiBold>
                                </Button>
                            )}
                            {!inModal && slackUrl && (
                                <Lib.Button.Ghost
                                    className="payment-request-item__slack-link"
                                    type="button"
                                    onClick={() => {
                                        openLinkInNewTab(slackUrl);
                                    }}
                                >
                                    <Label.Small.SemiBold className="payment-request-item__slack-link-text">
                                        Open in Slack
                                    </Label.Small.SemiBold>
                                </Lib.Button.Ghost>
                            )}
                        </div>
                    </Lib.Flex.Row>
                </Lib.Flex.Column>
            </Lib.Flex.Row>

            {showChargeButton && (
                <PaymentConfirmDialog
                    open={chargeConfirmOpen}
                    onOpenChange={handleChargeDialogOpenChange}
                    title="Charge this payment?"
                    description={`This will charge ${convertPriceToString(price)} to the member. Please confirm your choice before proceeding.`}
                    error={chargeError}
                    isPending={isChargePending}
                    confirmLabel="Charge"
                    pendingLabel="Charging…"
                    onConfirm={handleCharge}
                />
            )}

            {showMarkAsPaidButton && (
                <PaymentConfirmDialog
                    open={markAsPaidConfirmOpen}
                    onOpenChange={handleMarkAsPaidDialogOpenChange}
                    title="Mark this payment as paid?"
                    description={`This will mark ${convertPriceToString(price)} as paid. Use this only after the payment has been received.`}
                    error={markAsPaidError}
                    isPending={isMarkAsPaidPending}
                    confirmLabel="Mark as paid"
                    pendingLabel="Marking…"
                    onConfirm={handleMarkAsPaid}
                />
            )}
        </div>
    );
}

function formatPaymentStatus(status: PaymentStatus): string {
    switch (status) {
        case "PAYMENT_REQUESTED":
            return "Payment requested";
        case "DRAFT_INVOICE_CREATED":
            return "Draft invoice created";
        case "INVOICE_FINALIZED":
            return "Invoice finalized";
        case "INVOICE_PAID":
            return "Invoice paid";
        default: {
            const _exhaustive: never = status;
            return _exhaustive;
        }
    }
}
